import React, { useCallback, useEffect, useRef, useState } from "react";
import SenderEmailIcon from "../../../../../Assets/SettingsPages/SenderEmailIcon.svg";
import { ReactComponent as CloseIcon } from "../../../../../Assets/ActionIcons/CloseIcon.svg";
import PostMarkSignatureEmailTile from "./PostMarkSignatureEmailTile";
import { useSettingsStore } from "../../../../../Store/Settings";
import { useAuth0 } from "@auth0/auth0-react";

const PostMarkSignatureManagement: React.FC = () => {
  const { settings, createPostmarkSignature, isLoading } = useSettingsStore();
  const [signatureCreatedId, setSignatureCreatedId] = useState(0);
  const [addSenderEmailModalOpen, setAddSenderEmailModalOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, getAccessTokenSilently } = useAuth0();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setAddSenderEmailModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);
  const [senderEmail, setSenderEmail] = useState("");
  const [returnPath, setReturnPath] = useState("pm-bounces");
  useEffect(() => {
    setSenderEmail((user && user["https://channelforce.com/user_authorization"]?.email) || "");
  }, [user]);

  const addSignatureClicked = useCallback(async () => {
    const responseId = await createPostmarkSignature(
      getAccessTokenSilently,
      senderEmail,
      returnPath
    );
    setSignatureCreatedId(responseId);
    setAddSenderEmailModalOpen(false);
  }, [getAccessTokenSilently, createPostmarkSignature, senderEmail, returnPath]);

  return (
    <>
      <div className="flex relative">
        <img src={SenderEmailIcon} alt="Sender Email" />
        <div className="font-semibold text-gray-900 text-m ml-2">Sender Email</div>
        {addSenderEmailModalOpen && (
          <div className="z-40">
            <div className="fixed top-0 left-0 flex items-center justify-center w-screen h-[44rem] opacity-100 z-20">
              <div ref={menuRef} className="bg-white flex flex-col rounded shadow-lg max-w-[48rem]">
                <div className="p-4 flex justify-between border-b border-gray-200 font-semibold text-lg">
                  <div>Add Your Sender Email</div>
                  <button
                    className="hover:scale-110 transition-transform duration-500"
                    onClick={() => {
                      setAddSenderEmailModalOpen(false);
                    }}
                  >
                    <CloseIcon className="w-3 h-3" />
                  </button>
                </div>
                <div className="grid grid-cols-4 w-[36rem] p-8 gap-4">
                  <label className="col-span-1">
                    Sender Email
                    <sup className="text-red-600 p-2">*</sup>
                  </label>
                  <input
                    className="p-2 border border-gray-200 rounded col-span-3"
                    value={senderEmail}
                    onChange={e => setSenderEmail(e.target.value)}
                    required
                  />
                  <label className="col-span-1">
                    Return Path
                    <sup className="text-red-600 p-2">*</sup>
                  </label>
                  <div className="col-span-3 flex flex-col space-y-1">
                    <input
                      className="p-2 border border-gray-200 rounded"
                      value={returnPath}
                      onChange={e => setReturnPath(e.target.value)}
                      required
                    />
                    <small className="text-gray-600">
                      Return Path is an advanced setting. We recommend you to use the default value.
                    </small>
                  </div>
                </div>
                <div className="flex justify-end border-t border-gray-200">
                  <button
                    className="text-blue-600 border border-gray-200 font-semibold rounded px-4 py-2 mx-2 my-4 hover:bg-gray-200 transition-colors duration-500"
                    onClick={() => {
                      setAddSenderEmailModalOpen(false);
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className={`bg-blue-600 text-white font-semibold rounded px-4 py-2 m-4 hover:bg-blue-500 transition-colors duration-500 ${
                      isLoading && "cursor-progress"
                    }`}
                    disabled={isLoading}
                    onClick={addSignatureClicked}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
            <div className="fixed top-0 left-0 w-screen h-screen z-10 flex items-center justify-center bg-gray-950 opacity-20" />
          </div>
        )}
      </div>
      {settings?.postmark_sender_signatures?.length === 0 ? (
        <div className="ml-8 my-6">
          <div>
            <div className="text-gray-700">
              <p className="text-gray-700">
                You can configure your own sender email address for all emails sent by Zomentum
                Payments. This will help to avoid emails going to spam, and your clients will be
                able to reply directly to all emails.
              </p>

              <p className="text-gray-700">
                After you added a new sender email, you will be asked to add a TXT and CNAME record
                to the DNS settings of your sender domain. This will allow us to verify that you own
                the domain and let us send emails on your behalf.
              </p>
            </div>
            <button
              type="button"
              className="px-4 py-2 mt-4 text-blue-500 border rounded border-blue-500  hover:text-blue-700"
              onClick={() => setAddSenderEmailModalOpen(true)}
            >
              Add Sender Email
            </button>
          </div>
        </div>
      ) : (
        <div className="ml-8 my-6 flex space-x-92 flex-wrap">
          {settings?.postmark_sender_signatures?.map(signature => (
            <PostMarkSignatureEmailTile
              signature={signature}
              key={signature.id}
              defaultDetailsOpen={signature.id === signatureCreatedId}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default PostMarkSignatureManagement;
